import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Render } from 'Components/Display';
import { InputActive } from 'v3/Components/Form/InputActive';
import { FieldError } from 'v3/Components/Form/FormSection';
import { PrimaryButton } from 'v3/Components/Shared/Buttons';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { useEnums } from 'Context';
import { CoreEnum } from 'Models';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { SecureComponentV3 } from 'Components/Security/SecureComponentV3';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { ROUTES } from 'Navigation';
import { toast } from 'react-toastify';
import { useDataPoster } from 'Utilities';
import './Reviewers.Header.scss';

const NAME_REGEX = /^[a-zA-Z\s-]+$/;
const STARTS_CAPITAL_REGEX = /^[A-Z]/;

const isNameValid = (name: string) =>
  name.trim().length > 0 &&
  name.length <= 100 &&
  STARTS_CAPITAL_REGEX.test(name) &&
  NAME_REGEX.test(name);

const getNameError = (name: string): string | undefined => {
  if (!name) return undefined;
  if (!STARTS_CAPITAL_REGEX.test(name)) return 'Must start with a capital letter';
  if (!NAME_REGEX.test(name)) return 'Only letters, spaces, and hyphens are allowed';
  if (name.length > 100) return 'Must be 100 characters or fewer';
  return undefined;
};

const AddReviewerDialog: React.FC<{ isOpen: boolean; toggle: () => void; reviewerType: CoreEnum }> = ({ isOpen, toggle, reviewerType }) => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  const [postData] = useDataPoster(ENDPOINTS.reviewers.base, undefined, false, '3.0');

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response: any = await postData({ firstName, lastName, reviewerType });
      if (response?.data?.id) {
        history.push(`${ROUTES.REVIEWER_PROFILE}/${response.data.id}`);
      } else if (response?.response?.status === 400) {
        toast.error('Please validate the highlighted fields');
        const errors: FieldError[] = Object.entries((response.response.data as any).errors ?? {}).map(
          ([key, value]) => ({ fieldId: key, error: (value as string[])[0] })
        );
        setFieldErrors(errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    setFieldErrors(prev => prev.filter(fe => fe.fieldId !== 'firstName' && fe.fieldId !== 'lastName'));
  }, [firstName, lastName]);

  return (
    <ConfirmDialog
      dialogVisible={isOpen}
      toggleDialog={toggle}
      className="add-reviewer-dialog"
      title="Add Reviewer"
      primaryButtonText={isSubmitting ? 'Adding...' : 'Add'}
      primaryButtonEnabled={!isSubmitting && isNameValid(firstName) && isNameValid(lastName)}
      OnPrimaryButtonClick={handleConfirm}
      secondaryButtonText="Cancel"
      secondaryButtonEnabled={true}
      OnSecondaryButtonClick={toggle}
    >
      <div className="add-reviewer-body">
        <p className="add-reviewer-body__description">Please provide the name of the new reviewer.</p>
        <div className="add-reviewer-body__fields">
          <InputActive
            id="firstName"
            name="firstName"
            label="First Name"
            value={firstName}
            onChange={(e: any) => setFirstName(e.target.value)}
            required
            error={fieldErrors.find(f => f.fieldId === 'firstName')?.error ?? getNameError(firstName)}
          />
          <InputActive
            id="lastName"
            name="lastName"
            label="Last Name"
            value={lastName}
            onChange={(e: any) => setLastName(e.target.value)}
            required
            error={fieldErrors.find(f => f.fieldId === 'lastName')?.error ?? getNameError(lastName)}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
};

const NewReviewerButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<CoreEnum | null>(null);
  const { reviewerType } = useEnums();

  return (
    <>
      <div className="buttonWrapper">
        <PrimaryButton onClick={() => setIsOpen(prev => !prev)} className={isOpen ? 'open' : ''}>
          <div className="title">Add Reviewer</div>
          <span className="arrowIcon" style={{ padding: 0 }}>
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </span>
        </PrimaryButton>
        {isOpen && (
          <ul className="newReferralList">
            {Object.values(reviewerType).map(type => (
              <li key={type.value} onClick={() => { setSelectedType(type); setIsOpen(false); }}>
                <span className="linkText">{type.displayName}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Render condition={!!selectedType}>
        <AddReviewerDialog isOpen={!!selectedType} toggle={() => setSelectedType(null)} reviewerType={selectedType!} />
      </Render>
    </>
  );
};

const ReviewersListHeader: React.FC = () => (
  <div className="reviewers-list-header">
    <h1 className="reviewers-list-header__title">Reviewer Panel</h1>
    <div className="reviewers-list-header__actions">
      <SecureComponentV3 permissions={[PermissionCodesV3.Reviewer.Profile.Create]}>
        <NewReviewerButton />
      </SecureComponentV3>
    </div>
  </div>
);

export default ReviewersListHeader;
